/**
 * Authorization Context Provider
 *
 * Manages user permissions, roles, and delegations for the application.
 * Supports two launch modes:
 *  - standalone: Auth0 authentication; org + permissions from Auth0 claims and /auth/permissions.
 *  - epic_smart: Epic SMART on FHIR; org from EpicAuthClient session; permissions fetched
 *    using the provider-api JWT obtained via /auth/epic-provision.
 */

import { useAuth0 } from '@auth0/auth0-react'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { useLaunchMode } from '@/features/auth/launchContext'
import { ReactNode, useEffect, useState } from 'react'

import {
  AuthorizationContext,
  type AuthorizationContextType,
  type Delegation,
  type User,
} from './authorizationHooks'

interface AuthorizationProviderProps {
  children: ReactNode
}

function getEpicClientSafe(): EpicAuthClient | null {
  try {
    return EpicAuthClient.getInstance()
  } catch {
    return null
  }
}

export function AuthorizationProvider({ children }: AuthorizationProviderProps) {
  const { user, isAuthenticated, isLoading: auth0Loading, getAccessTokenSilently } = useAuth0()
  const { mode: launchMode, epicState } = useLaunchMode()

  const [permissions, setPermissions] = useState<string[]>([])
  const [roles, setRoles] = useState<string[]>([])
  const [availableDelegations, setAvailableDelegations] = useState<Delegation[]>([])
  const [activeDelegation, setActiveDelegation] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Auth0 custom claim (standalone mode). Display name for the org is sourced from
  // the microapp via ``getClientName(useUiConfig().config)``, not from a JWT claim.
  const auth0OrgId = user?.['https://inflectionmedicine.com/org_id'] as string | null

  // Epic session data (epic_smart mode)
  const epicClient = getEpicClientSafe()
  const epicContext =
    launchMode === 'epic_smart' && epicState && epicClient ? epicClient.getContext(epicState) : null
  // In epic_smart mode, orgId is the client onboarding key (e.g. Epic "11112") so FHIR
  // and Dynamo lookups align. IMDB membership UUID is on the provisioned JWT (and
  // EpicAuthClient.imdbOrganizationId); ordering resolves body org to that UUID server-side.
  const epicOrgId =
    launchMode === 'epic_smart' && epicState && epicClient ? epicClient.getOrgId(epicState) : null

  // Effective org: in epic_smart mode always derive from the Epic session so
  // that a cached Auth0 standalone session cannot override the Epic org.
  const orgId = launchMode === 'epic_smart' ? epicOrgId : auth0OrgId

  useEffect(() => {
    if (launchMode === 'epic_smart' && epicState) {
      fetchEpicPermissions(epicState)
    } else if (isAuthenticated && !auth0Loading) {
      // Fetch permissions whenever authenticated, EVEN with no orgId. An org-less
      // Case Connect participant (Path B) has orgId=null but still resolves
      // case:* server-side from their case membership; gating this fetch on orgId
      // would leave their permissions empty and deny every guard. Delegations are
      // org-scoped, so only fetch those when an org is present.
      fetchPermissions()
      if (orgId) {
        fetchDelegations()
      }
    } else if (!auth0Loading) {
      // Reset authorization state when no active session, so a stale
      // org_admin from a previous user/login cannot persist (GEN-3078).
      setPermissions([])
      setRoles([])
      setAvailableDelegations([])
      setActiveDelegation(null)
      setIsLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, orgId, auth0Loading, launchMode, epicState])

  /**
   * Fetch permissions for an Epic SMART on FHIR session using the provider-api JWT
   * stored (or refreshed) via EpicAuthClient.
   */
  async function fetchEpicPermissions(state: string) {
    if (!epicClient) {
      setIsLoading(false)
      return
    }
    try {
      setIsLoading(true)
      const token = await epicClient.getOrRefreshProviderApiToken(state)
      if (!token) {
        setIsLoading(false)
        return
      }
      const apiBaseUrl = import.meta.env['VITE_PROVIDER_API_URL'] || import.meta.env['VITE_API_BASE_URL']
      const response = await fetch(`${apiBaseUrl}/auth/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch permissions: ${response.statusText}`)
      }
      const data = await response.json()
      setPermissions(data.permissions || [])
      setRoles(data.roles || [])
    } catch (error) {
      console.error('[AuthorizationContext] Failed to fetch Epic permissions:', error)
      setPermissions([])
      setRoles([])
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Fetch user's permissions from API (standalone / Auth0 mode).
   */
  async function fetchPermissions() {
    try {
      setIsLoading(true)
      const token = await getAccessTokenSilently()
      const apiBaseUrl = import.meta.env['VITE_PROVIDER_API_URL'] || import.meta.env['VITE_API_BASE_URL']

      const response = await fetch(`${apiBaseUrl}/auth/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch permissions: ${response.statusText}`)
      }

      const data = await response.json()
      setPermissions(data.permissions || [])
      setRoles(data.roles || [])
    } catch (error) {
      console.error('[AuthorizationContext] Failed to fetch permissions:', error)
      setPermissions([])
      setRoles([])
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Fetch available delegations (where current user is the grantee).
   */
  async function fetchDelegations() {
    try {
      const token = await getAccessTokenSilently()
      const apiBaseUrl = import.meta.env['VITE_PROVIDER_API_URL'] || import.meta.env['VITE_API_BASE_URL']

      const response = await fetch(`${apiBaseUrl}/auth/delegations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch delegations: ${response.statusText}`)
      }

      const data = await response.json()
      setAvailableDelegations(data || [])
    } catch (error) {
      console.error('Failed to fetch delegations:', error)
      setAvailableDelegations([])
    }
  }

  function hasPermission(permission: string): boolean {
    return permissions.includes(permission)
  }

  function hasAnyPermission(permissionList: string[]): boolean {
    return permissionList.some((p) => permissions.includes(p))
  }

  function hasAllPermissions(permissionList: string[]): boolean {
    return permissionList.every((p) => permissions.includes(p))
  }

  function hasRole(role: string): boolean {
    return roles.includes(role)
  }

  async function refreshPermissions() {
    if (launchMode === 'epic_smart' && epicState) {
      await fetchEpicPermissions(epicState)
    } else {
      await fetchPermissions()
      await fetchDelegations()
    }
  }

  // Build the user object for the context value. In epic_smart mode, populate
  // from Epic session context when Auth0 user is not available.
  const epicUser: User | null = epicContext
    ? {
        id: epicContext.epicUserId || epicContext.epicUserFhirId || '',
        email: epicContext.userEmail || undefined,
        name: epicContext.userName
          ? `${epicContext.userName} ${epicContext.userLastName || ''}`.trim()
          : undefined,
      }
    : null

  const value: AuthorizationContextType = {
    user: user
      ? {
          id: user.sub || '',
          email: user.email,
          name: user.name,
        }
      : epicUser,
    userId: user?.sub || epicContext?.epicUserId || null,
    practitionerId: null,
    orgId,
    permissions,
    roles,
    isLoading,
    activeDelegation,
    availableDelegations,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    refreshPermissions,
    setActiveDelegation,
  }

  return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>
}
